import { Component, Input, OnInit, OnChanges, SimpleChanges, signal, ChangeDetectionStrategy } from '@angular/core';
import { TimeUnit } from '../../../../../shared/components/date-picker/date-picker.component';
import { CommonModule } from '@angular/common';
import dayjs from 'dayjs';
import { ApiService } from '../../../../../core/services/api.service';
import { forkJoin } from 'rxjs';
import { CardsComponent } from '../../shared/cards/cards.component';
import { IconComponent } from '../../../../../shared/icons/icon.component';
import { IndividualToasterComponent } from "../../../../../shared/components/loader/loader.component";

export interface BusinessCard {
  key: string;
  heading: string;
  shortHeading: string;
  viewIndex?: number;
  cardIndex?: number;
  colSpan?: number;
  show?: boolean;
  color?: string;
  currentColor?: string;
  currentValue?: number;
  previousValue?: number;
  valueKey?: string | number;
  unit?: string;
  chartData?: any[];
  dateRange?: { start: string; end: string };
  data?: any; // Data formatted for app-cards component

  // Display toggles
  showToggle?: boolean;
  showProgress?: boolean;
  showTable?: boolean;
  showStaticsToggleButton?: boolean;
  showDedicatedHeading?: boolean;
  showToggles?: boolean;
  showlabels?: boolean;
  defaultView?: 'chart' | 'table';

  // Runtime state
  loading?: boolean;
  stats?: any;
  previousstats?: any;
  records?: any[];
  previousrecords?: any[];

  // Tooltip + UI
  tooltip?: any[];
  tooltipData?: any;
  headers?: any[];

  // Formatting
  statsKeys?: {
    left: {
      key: string;
      valueType: 'currency' | 'date' | 'number' | string;
      prefix?: string;
      valueTypeIndex?: number[];
    };
  };
  [key: string]: any;
}

type gridCols = `grid-cols-${number}`;

@Component({
  selector: 'app-payment-collection-cards',
  imports: [
    CommonModule,
    CardsComponent,
    IconComponent,
    IndividualToasterComponent
],
  templateUrl: './payment-collection-cards.component.html',
  host: { class: 'block h-full min-h-0 w-full flex-1' },
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: ``,
})
export class PaymentCollectionCardsComponent implements OnInit, OnChanges {
  isLoading = signal(false)
  @Input() dateRange: { start_date: string, end_date: string, previous_start_date: string | null, previous_end_date: string | null, unit: TimeUnit; } = {
    start_date: '',
    end_date: '',
    previous_start_date: null,
    previous_end_date: null,
    unit: 'day'
  };
  @Input() productServerName: string = 'denefits'; // Dynamic product server name
  @Input() requiresAccessToken: boolean = false; // Whether the product requires an access token
  @Input() cocu_id: number = 1;
  @Input() country_id: number = 1;

  @Input() config?: {
    datePickerParams: any;
    cards: any[];
    headers?: any[];
    valueKey?: string;
    cols?: gridCols;
    statsKeys?: { left: string };
    selectedContractType?: number[];
  };

  @Input() data?: any;

  /**
   * Optional filter that limits which of the configured `config.cards` this
   * instance renders and fetches.
   *
   * - When omitted (or empty) → behaves exactly as before, rendering and
   *   fetching every card declared in `config.cards` (default UI preserved).
   * - When provided → only cards whose `key` is in `onlyKeys` are rendered
   *   and only their matching APIs are called. Used by the parent
   *   Contracts Report dashboard so each card can live in its own
   *   customizable grid slot (Customize Layout / Pin to Custom Dashboard).
   */
  @Input() onlyKeys?: string[];

  businessCards: BusinessCard[] = [];
  
  // Expose Math to template
  Math = Math;

  constructor(private apiService: ApiService) {}

  ngOnInit(): void {
    // Use dateRange from config if available
    if (this.config?.datePickerParams) {
      this.dateRange = this.config.datePickerParams;
    }
    this.initializeCards();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update dateRange from config if it changes
    if (changes['config'] && this.config?.datePickerParams) {
      this.dateRange = this.config.datePickerParams;
    }

    // Whether the `config` input actually changed content this cycle.
    // The parent contracts-report template uses an inline
    // `[config]="{...}"` object literal that re-creates a fresh
    // reference on EVERY change-detection pass — without this
    // content-aware diff, Angular fires `ngOnChanges['config']` on
    // every parent CD and we re-fetched + redrew the chart even when
    // nothing meaningful had changed. That cascade was the visible
    // "graph card data binding is very slow" symptom in My Dashboard
    // after adding/removing a pinned card.
    const configContentChanged =
      !!changes['config'] &&
      !changes['config'].firstChange &&
      !PaymentCollectionCardsComponent._configContentEqual(
        changes['config'].previousValue,
        changes['config'].currentValue,
      );

    if ((changes['dateRange'] && !changes['dateRange'].firstChange) ||
        (changes['productServerName'] && !changes['productServerName'].firstChange) ||
        (changes['cocu_id'] && !changes['cocu_id'].firstChange) ||
        (changes['country_id'] && !changes['country_id'].firstChange)) {
      this.fetchAllCardData();
    } else if (configContentChanged) {
      this.initializeCards();
    } else if (changes['onlyKeys'] && !changes['onlyKeys'].firstChange) {
      // Re-initialize when the filter changes so the rendered + fetched
      // subset stays in sync with the parent's request.
      this.initializeCards();
    }
  }

  /**
   * Content-aware equality for the `config` input, used by
   * {@link ngOnChanges} to avoid re-initializing + refetching when the
   * parent merely produced a fresh inline-literal config object whose
   * fields are all the same as the previous one.
   *
   * Compares only the fields this component actually reads:
   *  - `cards` — checked by length + per-index `key` (cheap).
   *  - `datePickerParams` — checked via {@link _dateRangeEqual}.
   *  - `cols`, `valueKey` — primitive `===`.
   *  - `headers` — reference check, then JSON-string fallback (these
   *    are small static arrays in practice).
   *  - `selectedContractType` — element-wise number compare.
   */
  private static _configContentEqual(prev: any, curr: any): boolean {
    if (prev === curr) return true;
    if (!prev || !curr) return prev === curr;

    if (!PaymentCollectionCardsComponent._cardListEqual(prev.cards, curr.cards)) {
      return false;
    }
    if (!PaymentCollectionCardsComponent._dateRangeEqual(
        prev.datePickerParams,
        curr.datePickerParams,
      )) {
      return false;
    }
    if (prev.cols !== curr.cols) return false;
    if (prev.valueKey !== curr.valueKey) return false;
    if (!PaymentCollectionCardsComponent._headersEqual(prev.headers, curr.headers)) {
      return false;
    }
    if (!PaymentCollectionCardsComponent._contractTypeEqual(
        prev.selectedContractType,
        curr.selectedContractType,
      )) {
      return false;
    }
    return true;
  }

  private static _cardListEqual(a: any[] | undefined, b: any[] | undefined): boolean {
    if (a === b) return true;
    if (!a || !b) return a === b;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i]?.key !== b[i]?.key) return false;
    }
    return true;
  }

  private static _dateRangeEqual(a: any, b: any): boolean {
    if (a === b) return true;
    if (!a || !b) return a === b;
    return (
      a.start_date === b.start_date &&
      a.end_date === b.end_date &&
      a.previous_start_date === b.previous_start_date &&
      a.previous_end_date === b.previous_end_date &&
      a.unit === b.unit
    );
  }

  private static _headersEqual(a: any[] | undefined, b: any[] | undefined): boolean {
    if (a === b) return true;
    if (!a || !b) return a === b;
    if (a.length !== b.length) return false;
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  }

  private static _contractTypeEqual(
    a: number[] | undefined,
    b: number[] | undefined,
  ): boolean {
    if (a === b) return true;
    if (!a || !b) return a === b;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }

  private initializeCards(): void {
    if (!this.config?.cards) {
      this.businessCards = [];
      return;
    }

    // Honor an optional `onlyKeys` filter so the parent dashboard can host
    // each card in its own customizable grid slot. When the filter is
    // omitted (default), every configured card is initialized exactly like
    // before, preserving the original behavior.
    const sourceCards = this._filterCardsByOnlyKeys(this.config.cards);

    // Initialize cards with loading state
    this.businessCards = sourceCards.map(card => ({
      ...card,
      loading: true,
      currentValue: 0,
      previousValue: 0,
      chartData: [],
      dateRange: this.getFormattedDateRange(),
      currentColor: card.currentColor || 'bg-cyan-500',
    }));

    // Fetch data for all cards
    this.fetchAllCardData();
  }

  private fetchAllCardData(): void {
    if (!this.config?.cards || !this.dateRange?.start_date) {
      return;
    }
    this.isLoading.set(true);
    const hasPreviousData = this.dateRange.previous_start_date && this.dateRange.previous_end_date;

    // Clear existing data before fetching new data
    this.businessCards = this.businessCards.map(card => ({
      ...card,
      loading: true,
      currentValue: 0,
      previousValue: 0,
      data: {
        chartData: [],
        previousChartData: [],
        tableData: [],
        previousTableData: [],
        summary: { current: 0 }
      }
    }));

    // Create API calls for each card (current and previous)
    const apiCalls: any = {};

    // Restrict the API calls to the `onlyKeys` subset (when provided) so
    // splitting one component into multiple per-card instances doesn't
    // refetch the full set in every instance.
    const activeCards = this._filterCardsByOnlyKeys(this.config.cards);
    activeCards.forEach(card => {
      if (card.key) {
        apiCalls[`${card.key}_current`] = this.makeApiCall(card.key, false);
        
        if (hasPreviousData) {
          apiCalls[`${card.key}_previous`] = this.makeApiCall(card.key, true);
        }
      }
    });

    // Execute all API calls in parallel
    forkJoin(apiCalls).subscribe({
      next: (results) => {
        this.processAllCardData(results);
        this.isLoading.set(false)
      },
      error: (error) => {
        console.error('Error fetching card data:', error);
        // Set loading to false for all cards on error
        this.businessCards = this.businessCards.map(card => ({
          ...card,
          loading: false
        }));
        this.isLoading.set(false)
      }
    });
  }

  /**
   * Filters the configured `cards` by the `onlyKeys` input. When `onlyKeys`
   * is omitted/empty the full list is returned unchanged so existing
   * call-sites keep their original behavior.
   */
  private _filterCardsByOnlyKeys(cards: any[]): any[] {
    const filter = this.onlyKeys;
    if (!filter || !filter.length) return cards;
    return cards.filter((c) => c?.key && filter.includes(c.key));
  }

  private makeApiCall(endpoint: string, usePrevious: boolean) {
    const startTime = usePrevious ? this.dateRange.previous_start_date : this.dateRange.start_date;
    const endTime = usePrevious ? this.dateRange.previous_end_date : this.dateRange.end_date;

    const payload: any = {
      start_time: dayjs(startTime || new Date()).format('YYYY-MM-DD'),
      end_time: dayjs(endTime || new Date()).format('YYYY-MM-DD'),
      is_dummy: 0,
      cocu_id: this.cocu_id,
      country_id: this.country_id,
      // timezone: 'PST',
      unit: this.dateRange?.unit || 'day',
      outputFormat: 'db',
      ...(this.config?.selectedContractType && { selectedContractType: this.config.selectedContractType })
    };

    if (this.productServerName === 'credee') {
      payload['timezone'] = 'PDT';
    }

    return this.apiService.post(`/visionboard/stats/${endpoint}`, payload, {
      addAccessToken: this.requiresAccessToken,
      productServerName: this.productServerName,
      showErrorToaster: true,
      showSuccessToaster: false,
      showLoader: false, // Only show loader for current calls
      // showLoader: !usePrevious, // Only show loader for current calls
    });
  }

  private processAllCardData(results: any): void {
    const hasPreviousData = this.dateRange.previous_start_date && this.dateRange.previous_end_date;

    this.businessCards = this.businessCards.map(card => {
      const currentKey = `${card.key}_current`;
      const previousKey = `${card.key}_previous`;

      const currentData = results[currentKey];
      const previousData = results[previousKey];

      // Extract data from API response
      let currentValue = 0;
      let previousValue = 0;
      let chartData: any[] = [];
      let previousChartData: any[] = [];
      let tableData: any[] = [];
      let previousTableData: any[] = [];

      // Get the value key to extract (amount, value, count, etc.)
      const valueKey = this.getValueKey(card);

      if (currentData && currentData.is_error === 0) {
        // Extract value from stats using the card's valueKey
        currentValue = currentData.stats?.[valueKey] || 0;
        chartData = (currentData.records || currentData.data || []).map((record: any) => ({
          ...record,
          key: 'Current'
        }));
        tableData = this.addIconsToTableData(currentData.records || currentData.data || []);
        // Format dates for table display based on unit
        tableData = this.formatTableDates(tableData, this.dateRange.unit);
      }

      if (previousData && previousData.is_error === 0 && hasPreviousData) {
        // Extract value from stats using the card's valueKey only if previous dates are selected
        previousValue = previousData.stats?.[valueKey] || 0;
        previousChartData = (previousData.records || previousData.data || []).map((record: any) => ({
          ...record,
          key: 'Previous'
        }));
        previousTableData = this.addIconsToTableData(previousData.records || previousData.data || []);
        // Format dates for table display based on unit
        previousTableData = this.formatTableDates(previousTableData, this.dateRange.unit);
      }

      // For cardType 2 (platform contracts), merge current and previous data
      if (card['cardType'] === 2 && hasPreviousData) {
        const merged = this.mergePlatformData(tableData, previousTableData, valueKey);
        tableData = merged.tableData;
        previousTableData = merged.previousTableData;
      }

      // Calculate percentages for cardType 3 (Average Contract Value)
      if (card['cardType'] === 3 && hasPreviousData && tableData.length && previousTableData.length) {
        tableData = tableData.map((currentRow, index) => {
          const previousRow = previousTableData[index];
          if (previousRow && previousRow.value) {
            const currentVal = parseFloat(currentRow.value) || 0;
            const previousVal = parseFloat(previousRow.value) || 0;
            const percentage = previousVal !== 0 ? ((currentVal - previousVal) / previousVal) * 100 : 0;
            return { ...currentRow, percentage };
          }
          return currentRow;
        });
      }

      // Format data for app-cards component
      const cardData = {
        chartData,
        previousChartData,
        tableData,
        previousTableData,
        summary: {
          current: currentValue,
          // Only include previous if dates are selected
          ...(hasPreviousData && { previous: previousValue })
        }
      };

      return {
        ...card,
        loading: false,
        data: cardData,
        dateRange: this.getFormattedDateRange()
      };
    });
  }

  /**
   * Merge platform data from current and previous periods
   * Ensures all platforms from both periods are included in both arrays
   */
  private mergePlatformData(currentData: any[], previousData: any[], valueKey: string): { tableData: any[], previousTableData: any[] } {
    // Create maps for quick lookup by platform name
    const currentMap = new Map<string, any>();
    const previousMap = new Map<string, any>();

    // Populate current map
    currentData.forEach(item => {
      const key = item.name || item.platform;
      if (key) {
        currentMap.set(key, item);
      }
    });

    // Populate previous map
    previousData.forEach(item => {
      const key = item.name || item.platform;
      if (key) {
        previousMap.set(key, item);
      }
    });

    // Get all unique platform names
    const allPlatforms = new Set<string>([...currentMap.keys(), ...previousMap.keys()]);

    // Build merged arrays
    const mergedTableData: any[] = [];
    const mergedPreviousTableData: any[] = [];

    allPlatforms.forEach(platformName => {
      const currentItem = currentMap.get(platformName);
      const previousItem = previousMap.get(platformName);

      // Add to current table data (use current if exists, otherwise create with 0)
      if (currentItem) {
        mergedTableData.push(currentItem);
      } else {
        // Platform doesn't exist in current period, create entry with 0
        mergedTableData.push({
          name: platformName,
          [valueKey]: 0,
          icon: previousItem?.icon || 'monitor'
        });
      }

      // Add to previous table data (use previous if exists, otherwise create with 0)
      if (previousItem) {
        mergedPreviousTableData.push(previousItem);
      } else {
        // Platform doesn't exist in previous period, create entry with 0
        mergedPreviousTableData.push({
          name: platformName,
          [valueKey]: 0,
          icon: currentItem?.icon || 'monitor'
        });
      }
    });

    // Sort both arrays by previous value (descending), then by current value
    // This maintains the original sorting behavior based on previous period
    const sortedIndices = mergedTableData
      .map((item, index) => ({ 
        item, 
        index,
        currentValue: parseFloat(item[valueKey]) || 0,
        previousValue: parseFloat(mergedPreviousTableData[index][valueKey]) || 0
      }))
      .sort((a, b) => {
        // Sort by previous value first (descending)
        if (b.previousValue !== a.previousValue) {
          return b.previousValue - a.previousValue;
        }
        // Then by current value (descending)
        return b.currentValue - a.currentValue;
      });

    // Rebuild arrays in sorted order
    const sortedTableData = sortedIndices.map(({ item }) => item);
    const sortedPreviousTableData = sortedIndices.map(({ index }) => mergedPreviousTableData[index]);

    return {
      tableData: sortedTableData,
      previousTableData: sortedPreviousTableData
    };
  }

  private extractValue(response: any, valueKey?: string): number {
    if (!response) return 0;

    // If valueKey is specified, use it to extract the value
    if (valueKey) {
      const value = this.getNestedValue(response, valueKey);
      return typeof value === 'number' ? value : 0;
    }

    // Default extraction logic
    if (response.stats && typeof response.stats === 'object') {
      // Try common stat keys
      return response.stats.total || response.stats.count || response.stats.value || 0;
    }

    if (typeof response.total === 'number') return response.total;
    if (typeof response.count === 'number') return response.count;
    if (typeof response.value === 'number') return response.value;

    return 0;
  }

  private getFormattedDateRange(): { start: string; end: string } | undefined {
    if (!this.dateRange?.start_date || !this.dateRange?.end_date) {
      return undefined;
    }

    return {
      start: dayjs(this.dateRange.start_date).format('DD MMM'),
      end: dayjs(this.dateRange.end_date).format('DD MMM')
    };
  }

  private getNestedValue(obj: any, path: string): any {
    if (!obj || !path) return 0;
    
    return path.split('.').reduce((current, key) => {
      return current?.[key] ?? 0;
    }, obj);
  }

  // Helper method to get valueKey as string
  getValueKey(card: BusinessCard): string {
    if (typeof card.valueKey === 'string') {
      return card.valueKey;
    }
    if (this.config?.valueKey && typeof this.config.valueKey === 'string') {
      return this.config.valueKey;
    }
    return 'value';
  }

  // Helper method to get value format from card configuration
  getValueFormat(card: BusinessCard): 'currency' | 'number' | 'percent' {
    // Check if statsKeys exists and has valueType
    const valueType = card.statsKeys?.left?.valueType;
    
    if (!valueType) {
      return 'number'; // default
    }
    
    // Handle array of types (use first type)
    if (Array.isArray(valueType)) {
      const firstType = valueType[0];
      return firstType === 'currency' ? 'currency' : 'number';
    }
    
    // Handle string type
    if (valueType === 'currency') {
      return 'currency';
    } else if (valueType === 'number') {
      return 'number';
    }
    
    return 'number'; // default
  }

  // Helper method to get table headers
  getTableHeaders(card: BusinessCard): any[] {
    // Priority: card.headers > config.headers > generate default headers
    if (card.headers && card.headers.length > 0) {
      return card.headers;
    }
    
    if (this.config?.headers && this.config.headers.length > 0) {
      return this.config.headers;
    }

    // Generate default headers based on date range
    return this.generateDefaultHeaders();
  }

  // Generate default headers with date ranges
  private generateDefaultHeaders(): any[] {
    const unit = this.dateRange?.unit || 'day';
    const dateLabel = this.getDateLabel(unit);
    
    // Format date ranges for current and previous periods
    const currentPeriodLabel = this.dateRange?.start_date && this.dateRange?.end_date
      ? this.formatDateRange(this.dateRange.start_date, this.dateRange.end_date)
      : '';
    
    const previousPeriodLabel = this.dateRange?.previous_start_date && this.dateRange?.previous_end_date
      ? this.formatDateRange(this.dateRange.previous_start_date, this.dateRange.previous_end_date)
      : '';

    const headers: any[] = [
      { label: dateLabel, key: 'start', align: 'text-left', noTransform: true },
      { 
        label: `Current${currentPeriodLabel ? ` (${currentPeriodLabel})` : ''}`, 
        key: 'amount', 
        align: 'text-left',
        showProgress: true
      }
    ];

    // Add previous column if previous data exists
    if (this.dateRange?.previous_start_date && this.dateRange?.previous_end_date) {
      headers.push({
        label: `Previous${previousPeriodLabel ? ` (${previousPeriodLabel})` : ''}`,
        key: 'amount',
        align: 'text-left',
        isPrevious: true,
        showProgress: false
      });
    }

    return headers;
  }

  // Get date label based on unit
  private getDateLabel(unit: string): string {
    const labels: { [key: string]: string } = {
      'hour': 'Hour',
      'day': 'Date',
      'week': 'Week',
      'month': 'Month',
      'quarter': 'Quarter',
      'year': 'Year'
    };
    return labels[unit] || 'Date';
  }

  // Format date range for display
  formatDateRange(startDate: string, endDate: string): string {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    
    return `${start.format('MMM D')} - ${end.format('MMM D')}`;
  }

  // Format value based on type
  formatValue(value: any): string {
    if (value === null || value === undefined) return '-';
    
    const numValue = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(numValue)) return value;
    
    // Check if it's a currency value (assume values > 100 are currency)
    if (numValue >= 100 || numValue < -100) {
      const formatted = numValue.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      return `$${formatted}`;
    } else {
      // Otherwise format as number with commas
      return numValue.toLocaleString('en-US');
    }
  }

  // Add icons to table data based on platform name
  private addIconsToTableData(data: any[]): any[] {
    return data.map(row => {
      let icon = 'monitor'; // default icon
      
      const name = row.name?.toLowerCase() || '';
      
      if (name.includes('proposal')) {
        icon = 'file';
      } else if (name.includes('website') || name.includes('pay over time')) {
        icon = 'globe';
      } else if (name.includes('app')) {
        icon = 'smartphone';
      } else if (name.includes('business panel')) {
        icon = 'monitor';
      }
      
      return {
        ...row,
        icon
      };
    });
  }

  // Format table dates based on unit to show date ranges
  private formatTableDates(data: any[], unit?: string): any[] {
    if (!unit) return data;

    return data.map(row => {
      // If row has both start and end dates, format them as a range
      if (row.start && row.end) {
        const start = dayjs(row.start);
        const end = dayjs(row.end);
        let formattedStart = '';

        switch (unit) {
          case 'hour':
            // For hourly: show date with hour range
            if (start.isSame(end, 'day')) {
              formattedStart = `${start.format('MMM D')} (${start.format('HH:mm')} - ${end.format('HH:mm')})`;
            } else {
              formattedStart = `${start.format('MMM D HH:mm')} - ${end.format('MMM D HH:mm')}`;
            }
            break;
          
          case 'day':
            // For daily: just show the date without year
            formattedStart = start.format('MMM D');
            break;
          
          case 'week':
            // For weekly: show "14 Oct - 19 Oct" format
            if (start.isSame(end, 'month')) {
              formattedStart = `${start.format('DD MMM')} - ${end.format('DD MMM')}`;
            } else {
              formattedStart = `${start.format('DD MMM')} - ${end.format('DD MMM')}`;
            }
            break;
          
          case 'month':
            // For monthly: show month and year
            formattedStart = start.format('MMM YYYY');
            break;
          
          case 'quarter':
            // For quarterly: show "Jan 24 - Mar 24" format (start month - end month with year)
            formattedStart = `${start.format('MMM YY')} - ${end.format('MMM YY')}`;
            break;
          
          case 'year':
            // For yearly: show year
            formattedStart = start.format('YYYY');
            break;
          
          default:
            formattedStart = start.format('MMM D');
        }

        return {
          ...row,
          start: formattedStart
        };
      }

      return row;
    });
  }

  // Toggle between chart and table view for a specific card
  toggleView(cardKey: string, view: 'chart' | 'table'): void {
    this.businessCards = this.businessCards.map(card => {
      if (card.key === cardKey) {
        return { ...card, view };
      }
      return card;
    });
  }
}
